import { CommonModule } from '@angular/common';
import {
  Component,
  computed,
  ElementRef,
  inject,
  signal,
  viewChild,
} from '@angular/core';
import { FormsModule } from '@angular/forms';
import { TranslatePipe, TranslateService } from '@ngx-translate/core';
import { CtaButtonComponent } from '../../../../shared/components/ui/cta-button/cta-button.component';
import { InfoBannerComponent } from '../../../../shared/components/ui/info-banner/info-banner.component';
import { environment } from '../../../../../environments/environment';
import { getValvurApiError } from '../../../../core/errors/parse-valvur-api-error';
import {
  normalizeProtoInt64,
  resolveDisclosureScopeLabel,
} from '../../../identity/helpers/disclosure-scope.helper';
import { PatrolIntegrationService } from '../../services/patrol-integration.service';
import {
  INTEGRATION_ENVIRONMENTS,
  isIntegrationEditable,
  isIntegrationSubmittable,
  PARTNER_PROVIDER_TYPES,
  PartnerIntegrationRequest,
  PartnerProviderType,
  PartnerWebhookDelivery,
} from '../../models/partner-integration.model';

type OAuthEndpoint = {
  id: string;
  labelKey: string;
  url: string;
};

type ConnectStep = {
  titleKey: string;
  hintKey: string;
};

type FormState = {
  requestedClientId: string;
  appName: string;
  providerType: PartnerProviderType;
  redirectUris: string[];
  scopeIds: string[];
  environment: 'sandbox' | 'production';
  webhookUrl: string;
};

const EMPTY_FORM: FormState = {
  requestedClientId: '',
  appName: '',
  providerType: 'custom',
  redirectUris: [''],
  scopeIds: [],
  environment: 'sandbox',
  webhookUrl: '',
};

const ONBOARDING_STEPS = [
  {
    titleKey: 'app.patrol.integrationRequest.onboarding.step1Title',
    hintKey: 'app.patrol.integrationRequest.onboarding.step1Hint',
  },
  {
    titleKey: 'app.patrol.integrationRequest.onboarding.step2Title',
    hintKey: 'app.patrol.integrationRequest.onboarding.step2Hint',
  },
  {
    titleKey: 'app.patrol.integrationRequest.onboarding.step3Title',
    hintKey: 'app.patrol.integrationRequest.onboarding.step3Hint',
  },
  {
    titleKey: 'app.patrol.integrationRequest.onboarding.step4Title',
    hintKey: 'app.patrol.integrationRequest.onboarding.step4Hint',
  },
] as const;

const CONNECT_STEPS: readonly ConnectStep[] = [
  {
    titleKey: 'app.patrol.integrationRequest.connect.step1Title',
    hintKey: 'app.patrol.integrationRequest.connect.step1Hint',
  },
  {
    titleKey: 'app.patrol.integrationRequest.connect.step2Title',
    hintKey: 'app.patrol.integrationRequest.connect.step2Hint',
  },
  {
    titleKey: 'app.patrol.integrationRequest.connect.step3Title',
    hintKey: 'app.patrol.integrationRequest.connect.step3Hint',
  },
  {
    titleKey: 'app.patrol.integrationRequest.connect.step4Title',
    hintKey: 'app.patrol.integrationRequest.connect.step4Hint',
  },
] as const;

const STATUS_ORDER = [
  'draft',
  'submitted',
  'needs_info',
  'approved',
  'rejected',
] as const;

const SCOPE_PREVIEW_LIMIT = 3;
const COPY_FEEDBACK_MS = 2000;

@Component({
  selector: 'app-patrol-integration-request',
  standalone: true,
  imports: [
    CommonModule,
    FormsModule,
    TranslatePipe,
    CtaButtonComponent,
    InfoBannerComponent,
  ],
  templateUrl: './patrol-integration-request.component.html',
  styleUrl: './patrol-integration-request.component.scss',
})
export class PatrolIntegrationRequestComponent {
  #integration = inject(PatrolIntegrationService);
  #translate = inject(TranslateService);

  readonly requestsSectionRef = viewChild<ElementRef<HTMLElement>>('requestsSection');

  readonly providerTypes = PARTNER_PROVIDER_TYPES;
  readonly environments = INTEGRATION_ENVIRONMENTS;
  readonly onboardingSteps = ONBOARDING_STEPS;
  readonly connectSteps = CONNECT_STEPS;

  readonly requests = computed(() => this.#integration.requests());
  readonly requestCount = computed(() => this.requests().length);
  readonly oauthApiBaseUrl = environment.baseUrl.replace(/\/$/, '');
  readonly oauthEndpoints: readonly OAuthEndpoint[] = [
    {
      id: 'discovery',
      labelKey: 'app.patrol.integrationRequest.connect.endpointDiscovery',
      url: `${this.oauthApiBaseUrl}/.well-known/openid-configuration`,
    },
    {
      id: 'authorize',
      labelKey: 'app.patrol.integrationRequest.connect.endpointAuthorize',
      url: `${this.oauthApiBaseUrl}/oauth/authorize`,
    },
    {
      id: 'token',
      labelKey: 'app.patrol.integrationRequest.connect.endpointToken',
      url: `${this.oauthApiBaseUrl}/oauth/token`,
    },
    {
      id: 'userinfo',
      labelKey: 'app.patrol.integrationRequest.connect.endpointUserinfo',
      url: `${this.oauthApiBaseUrl}/oauth/userinfo`,
    },
    {
      id: 'introspect',
      labelKey: 'app.patrol.integrationRequest.connect.endpointIntrospect',
      url: `${this.oauthApiBaseUrl}/oauth/introspect`,
    },
  ];
  readonly statusSummary = computed(() => {
    const counts = new Map<string, number>();

    for (const request of this.requests()) {
      const status = request.status ?? 'unknown';
      counts.set(status, (counts.get(status) ?? 0) + 1);
    }

    return STATUS_ORDER.filter((status) => (counts.get(status) ?? 0) > 0).map(
      (status) => ({
        status,
        count: counts.get(status) ?? 0,
      }),
    );
  });
  readonly scopeItems = computed(
    () => this.#integration.scopeCatalogQuery.data()?.items ?? [],
  );
  readonly isLoading = computed(
    () =>
      this.#integration.requestsQuery.isPending() ||
      this.#integration.scopeCatalogQuery.isPending(),
  );
  readonly showLoadError = computed(
    () =>
      this.#integration.requestsQuery.isError() ||
      this.#integration.scopeCatalogQuery.isError(),
  );

  readonly editingRequestId = signal<string | null>(null);
  readonly showForm = signal(false);
  formState: FormState = {
    ...EMPTY_FORM,
    redirectUris: [...EMPTY_FORM.redirectUris],
  };
  readonly pageError = signal<string | null>(null);
  readonly actingRequestId = signal<string | null>(null);
  readonly saving = signal(false);
  readonly copiedKey = signal<string | null>(null);
  /** One-time revealed signing secrets by request id (never re-fetched). */
  readonly revealedWebhookSecrets = signal<Record<string, string>>({});
  readonly webhookDrafts = signal<Record<string, string>>({});
  readonly webhookSavingId = signal<string | null>(null);
  readonly webhookDeliveries = signal<Record<string, PartnerWebhookDelivery[]>>(
    {},
  );
  readonly webhookDeliveriesLoadingId = signal<string | null>(null);
  readonly webhookResendingId = signal<string | null>(null);

  isApproved(request: PartnerIntegrationRequest): boolean {
    return request.status === 'approved' || !!request.oauthClientId;
  }

  registeredClientId(request: PartnerIntegrationRequest): string {
    return (request.oauthClientId || request.requestedClientId || '').trim();
  }

  sampleAuthorizeUrl(request: PartnerIntegrationRequest): string | null {
    const clientId = this.registeredClientId(request);
    const redirectUri = (request.redirectUris ?? []).find((uri) => !!uri.trim());
    const scopes = (request.scopeIds ?? []).filter(Boolean);
    if (!clientId || !redirectUri || scopes.length === 0) {
      return null;
    }

    const params = new URLSearchParams({
      client_id: clientId,
      redirect_uri: redirectUri.trim(),
      response_type: 'code',
      scope: scopes.join(' '),
      code_challenge_method: 'S256',
    });

    // Keep PKCE placeholders readable for partners copying the sample.
    return `${this.oauthApiBaseUrl}/oauth/authorize?${params.toString()}&code_challenge={CODE_CHALLENGE}&state={STATE}`;
  }

  async copyText(value: string, key: string): Promise<void> {
    const text = value.trim();
    if (!text) {
      return;
    }

    try {
      await navigator.clipboard.writeText(text);
      this.copiedKey.set(key);
      setTimeout(() => {
        if (this.copiedKey() === key) {
          this.copiedKey.set(null);
        }
      }, COPY_FEEDBACK_MS);
    } catch {
      this.copiedKey.set(null);
    }
  }

  isCopied(key: string): boolean {
    return this.copiedKey() === key;
  }

  scopeLabel(scopeId: string): string {
    return resolveDisclosureScopeLabel(scopeId, (key) =>
      this.#translate.instant(key),
    );
  }

  isEditable(request: PartnerIntegrationRequest): boolean {
    return isIntegrationEditable(request.status);
  }

  isSubmittable(request: PartnerIntegrationRequest): boolean {
    return isIntegrationSubmittable(request.status);
  }

  statusLabel(status: string | undefined): string {
    const key = `app.patrol.integrationRequest.status.${status ?? 'unknown'}`;
    const translated = this.#translate.instant(key);
    return translated !== key ? translated : (status ?? '—');
  }

  providerLabel(providerType: string | undefined): string {
    const type = providerType ?? 'custom';
    const key = `app.patrol.integrationRequest.provider.${type}`;
    const translated = this.#translate.instant(key);
    return translated !== key ? translated : type;
  }

  environmentLabel(environment: string | undefined): string {
    const env = environment === 'production' ? 'production' : 'sandbox';
    const key = `app.patrol.integrationRequest.environment.${env}`;
    return this.#translate.instant(key);
  }

  appInitial(appName: string | undefined): string {
    const trimmed = (appName ?? '').trim();
    return trimmed ? trimmed.charAt(0).toUpperCase() : '?';
  }

  scopePreview(request: PartnerIntegrationRequest): {
    visible: string[];
    overflow: number;
  } {
    const scopeIds = request.scopeIds ?? [];
    return {
      visible: scopeIds.slice(0, SCOPE_PREVIEW_LIMIT),
      overflow: Math.max(0, scopeIds.length - SCOPE_PREVIEW_LIMIT),
    };
  }

  requestCountLabel(): string {
    const count = this.requestCount();
    const key =
      count === 1
        ? 'app.patrol.integrationRequest.requestsCount'
        : 'app.patrol.integrationRequest.requestsCount_plural';
    return this.#translate.instant(key, { count });
  }

  formatTimestamp(value: unknown): Date | null {
    const unixSeconds = normalizeProtoInt64(value);
    if (unixSeconds <= 0) {
      return null;
    }
    const date = new Date(unixSeconds * 1000);
    return Number.isNaN(date.getTime()) ? null : date;
  }

  openCreateForm(): void {
    this.editingRequestId.set(null);
    this.formState = {
      ...EMPTY_FORM,
      redirectUris: [''],
    };
    this.pageError.set(null);
    this.showForm.set(true);
    this.focusFormSection();
  }

  openEditForm(request: PartnerIntegrationRequest): void {
    if (!request.id || !this.isEditable(request)) {
      return;
    }

    const uris = [...(request.redirectUris ?? [])].filter(Boolean);
    this.editingRequestId.set(request.id);
    this.formState = {
      requestedClientId: request.requestedClientId ?? '',
      appName: request.appName ?? '',
      providerType:
        (request.providerType as PartnerProviderType) ?? 'custom',
      redirectUris: uris.length > 0 ? uris : [''],
      scopeIds: [...(request.scopeIds ?? [])],
      environment:
        request.environment === 'production' ? 'production' : 'sandbox',
      webhookUrl: request.webhookUrl ?? '',
    };
    this.pageError.set(null);
    this.showForm.set(true);
    this.focusFormSection();
  }

  addRedirectUri(): void {
    this.formState = {
      ...this.formState,
      redirectUris: [...this.formState.redirectUris, ''],
    };
  }

  removeRedirectUri(index: number): void {
    const next = this.formState.redirectUris.filter((_, i) => i !== index);
    this.formState = {
      ...this.formState,
      redirectUris: next.length > 0 ? next : [''],
    };
  }

  updateRedirectUri(index: number, value: string): void {
    const redirectUris = [...this.formState.redirectUris];
    redirectUris[index] = value;
    this.formState = { ...this.formState, redirectUris };
  }

  webhookDraft(request: PartnerIntegrationRequest): string {
    const id = request.id ?? '';
    if (!id) {
      return '';
    }
    const drafts = this.webhookDrafts();
    if (Object.prototype.hasOwnProperty.call(drafts, id)) {
      return drafts[id];
    }
    return request.webhookUrl ?? '';
  }

  setWebhookDraft(requestId: string, value: string): void {
    this.webhookDrafts.update((current) => ({ ...current, [requestId]: value }));
  }

  revealedWebhookSecret(requestId: string | undefined): string | null {
    if (!requestId) {
      return null;
    }
    return this.revealedWebhookSecrets()[requestId] ?? null;
  }

  async saveWebhook(request: PartnerIntegrationRequest): Promise<void> {
    if (!request.id) {
      return;
    }
    const webhookUrl = this.webhookDraft(request).trim();
    if (!webhookUrl) {
      this.pageError.set(
        this.#translate.instant(
          'app.patrol.integrationRequest.webhook.urlRequired',
        ),
      );
      return;
    }

    this.webhookSavingId.set(request.id);
    this.pageError.set(null);
    try {
      const result = await this.#integration.configureWebhookMutation.mutateAsync(
        {
          requestId: request.id,
          webhookUrl,
        },
      );
      if (result.webhookSecret) {
        this.revealedWebhookSecrets.update((current) => ({
          ...current,
          [request.id!]: result.webhookSecret!,
        }));
      }
      this.webhookDrafts.update((current) => {
        const next = { ...current };
        delete next[request.id!];
        return next;
      });
    } catch (error) {
      this.pageError.set(
        getValvurApiError(error)?.message ??
          this.#translate.instant('app.patrol.integrationRequest.actionError'),
      );
    } finally {
      this.webhookSavingId.set(null);
    }
  }

  deliveriesFor(request: PartnerIntegrationRequest): PartnerWebhookDelivery[] {
    const id = request.id ?? '';
    return id ? (this.webhookDeliveries()[id] ?? []) : [];
  }

  async loadWebhookDeliveries(request: PartnerIntegrationRequest): Promise<void> {
    if (!request.id || !this.isApproved(request)) {
      return;
    }
    this.webhookDeliveriesLoadingId.set(request.id);
    this.pageError.set(null);
    try {
      const items = await this.#integration.fetchWebhookDeliveries(request.id);
      this.webhookDeliveries.update((current) => ({
        ...current,
        [request.id!]: items,
      }));
    } catch (error) {
      this.pageError.set(
        getValvurApiError(error)?.message ??
          this.#translate.instant('app.patrol.integrationRequest.actionError'),
      );
    } finally {
      this.webhookDeliveriesLoadingId.set(null);
    }
  }

  async resendWebhookDelivery(
    request: PartnerIntegrationRequest,
    delivery: PartnerWebhookDelivery,
  ): Promise<void> {
    if (!request.id || !delivery.id) {
      return;
    }
    this.webhookResendingId.set(delivery.id);
    this.pageError.set(null);
    try {
      await this.#integration.resendWebhookMutation.mutateAsync({
        requestId: request.id,
        deliveryId: delivery.id,
      });
      await this.loadWebhookDeliveries(request);
    } catch (error) {
      this.pageError.set(
        getValvurApiError(error)?.message ??
          this.#translate.instant('app.patrol.integrationRequest.actionError'),
      );
    } finally {
      this.webhookResendingId.set(null);
    }
  }

  private focusFormSection(): void {
    // Avoid `afterNextRender()` since this method is invoked from template events
    // (not always within an injection context). A microtask + rAF is enough to
    // wait for the DOM to update after `showForm` flips.
    queueMicrotask(() => {
      requestAnimationFrame(() => {
        this.requestsSectionRef()?.nativeElement.scrollIntoView({
          behavior: 'smooth',
          block: 'start',
        });
      });
    });
  }

  cancelForm(): void {
    this.showForm.set(false);
    this.editingRequestId.set(null);
    this.formState = {
      ...EMPTY_FORM,
      redirectUris: [''],
    };
    this.pageError.set(null);
  }

  toggleScope(scopeId: string, checked: boolean): void {
    const scopeIds = new Set(this.formState.scopeIds);
    if (checked) {
      scopeIds.add(scopeId);
    } else {
      scopeIds.delete(scopeId);
    }
    this.formState = { ...this.formState, scopeIds: [...scopeIds] };
  }

  isScopeSelected(scopeId: string): boolean {
    return this.formState.scopeIds.includes(scopeId);
  }

  async saveForm(): Promise<void> {
    this.saving.set(true);
    this.pageError.set(null);

    const current = this.formState;
    const dto = {
      requestedClientId: current.requestedClientId.trim().toLowerCase(),
      appName: current.appName.trim(),
      providerType: current.providerType,
      redirectUris: current.redirectUris
        .map((line) => line.trim())
        .filter(Boolean),
      scopeIds: current.scopeIds,
      environment: current.environment,
      webhookUrl: current.webhookUrl.trim(),
    };

    try {
      const editingId = this.editingRequestId();
      if (editingId) {
        await this.#integration.updateMutation.mutateAsync({
          requestId: editingId,
          dto,
        });
      } else {
        await this.#integration.createMutation.mutateAsync(dto);
      }
      this.cancelForm();
    } catch (error) {
      this.pageError.set(
        getValvurApiError(error)?.message ??
          this.#translate.instant('app.patrol.integrationRequest.actionError'),
      );
    } finally {
      this.saving.set(false);
    }
  }

  async submitRequest(request: PartnerIntegrationRequest): Promise<void> {
    if (!request.id || !this.isSubmittable(request)) {
      return;
    }

    this.actingRequestId.set(request.id);
    this.pageError.set(null);

    try {
      await this.#integration.submitMutation.mutateAsync(request.id);
    } catch (error) {
      this.pageError.set(
        getValvurApiError(error)?.message ??
          this.#translate.instant('app.patrol.integrationRequest.actionError'),
      );
    } finally {
      this.actingRequestId.set(null);
    }
  }

  isActing(requestId: string | undefined): boolean {
    return !!requestId && this.actingRequestId() === requestId;
  }
}
